import { c as _c } from "react/compiler-runtime";
import React, { Suspense, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useClickAway } from 'react-use';
import JBImage from 'components/essentials/JBImage';
import SanitizedHtml from 'components/consumer/SanitizedHtml';
import IconClose from 'components/icons/iconClose';
import IconSimpleArrowLeft from 'components/icons/iconSimpleArrowLeft';
import useFitGuideSlides from 'data-hooks/useFitGuideSlides';
import { PDP_CAROUSEL, PRODUCT_INFO_INTERACTION, trackCarouselNavigated, trackProductInfoInteracted } from 'data-hooks/useEvents/pdpInteractionEvents';
import useIsBrowser from 'hooks/useIsBrowser';
import { prefersReducedMotion } from 'hooks/useScrollSelectedIntoView';

// Edge copies let a swipe past either end loop round
const getTrackSlides = slides => {
  const trackSlides = slides.map((slide, position) => ({
    ...slide,
    key: `slide-${position}`
  }));
  return {
    trackSlides,
    loopTrackSlides: [{
      ...slides[slides.length - 1],
      key: 'loop-start'
    }, ...trackSlides, {
      ...slides[0],
      key: 'loop-end'
    }]
  };
};

// Invisible: an edge copy shows the same picture as its real slide
const jumpOffEdgeCopy = (track, position, slideCount) => {
  const loopWidth = slideCount * track.clientWidth;
  if (position <= 0) {
    track.scrollLeft += loopWidth;
    return slideCount;
  }
  if (position >= slideCount + 1) {
    track.scrollLeft -= loopWidth;
    return 1;
  }
  return position;
};
const ArrowButton = t0 => {
  const $ = _c(6);
  const {
    direction,
    onClick
  } = t0;
  const t1 = direction === "prev" ? "Previous slide" : "Next slide";
  const t2 = direction === "next" ? "rotate-180" : undefined;
  let t3;
  if ($[0] !== t2) {
    t3 = <IconSimpleArrowLeft stroke="currentColor" width={9} height={15} className={t2} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== onClick || $[3] !== t1 || $[4] !== t3) {
    t4 = <button type="button" aria-label={t1} onClick={onClick} className="pointer-events-auto flex h-11 w-11 flex-none items-center justify-center rounded-full border border-solid border-gray-light6 bg-white p-0 hover:cursor-pointer hover:border-brand">{t3}</button>;
    $[2] = onClick;
    $[3] = t1;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
ArrowButton.propTypes = {
  direction: PropTypes.oneOf(['prev', 'next']).isRequired,
  onClick: PropTypes.func
};

// Same native scroll-snap approach as PDPSectionCarousel, one slide per page
const FitGuideSlideshow = t0 => {
  const $ = _c(72);
  const {
    slides
  } = t0;
  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const slideCount = slides.length;
  let t1;
  if ($[0] !== slides) {
    t1 = getTrackSlides(slides);
    $[0] = slides;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    trackSlides: realSlides,
    loopTrackSlides
  } = t1;
  const looping = useIsBrowser() && slideCount > 1;
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const readMoreRef = useRef(null);
  const frameRef = useRef(0);
  const scrollEndRef = useRef(0);
  const positionRef = useRef(0);
  const navigatedSlideRef = useRef(0);
  const bodyId = useId();
  const slide = slides[index];
  const offset = looping ? 1 : 0;
  const trackSlides = looping ? loopTrackSlides : realSlides;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      cancelAnimationFrame(frameRef.current);
      clearTimeout(scrollEndRef.current);
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== looping) {
    t4 = () => {
      const track = trackRef.current;
      if (!looping || !track) {
        return;
      }
      positionRef.current = 1;
      const syncScroll = () => {
        track.scrollLeft = positionRef.current * track.clientWidth;
      };
      const observer = new ResizeObserver(syncScroll);
      syncScroll();
      observer.observe(track);
      return () => observer.disconnect();
    };
    t5 = [looping];
    $[4] = looping;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useLayoutEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = t7 => {
      const {
        restoreFocus
      } = t7;
      setExpanded(false);
      trackProductInfoInteracted(PRODUCT_INFO_INTERACTION.EXPANDED_CONTENT_CLOSED);
      if (restoreFocus) {
        readMoreRef.current?.focus();
      }
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const closeBody = t6;
  let t7;
  if ($[8] !== expanded) {
    t7 = () => {
      setExpanded(_temp);
      trackProductInfoInteracted(expanded ? PRODUCT_INFO_INTERACTION.READ_LESS_COLLAPSED : PRODUCT_INFO_INTERACTION.READ_MORE_EXPANDED);
    };
    $[8] = expanded;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const toggleBody = t7;
  let t8;
  if ($[10] !== expanded) {
    t8 = () => {
      if (expanded) {
        closeBody({
          restoreFocus: false
        });
      }
    };
    $[10] = expanded;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  useClickAway(sectionRef, t8);
  let t10;
  let t9;
  if ($[12] !== expanded) {
    t9 = () => {
      if (!expanded) {
        return;
      }
      const handleKeyDown = event => {
        if (event.key === "Escape") {
          closeBody({
            restoreFocus: sectionRef.current?.contains(document.activeElement)
          });
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t10 = [expanded];
    $[12] = expanded;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  if ($[15] !== looping || $[16] !== offset || $[17] !== slideCount) {
    t11 = () => {
      const track_0 = trackRef.current;
      if (!track_0?.clientWidth) {
        return;
      }
      const position = Math.round(track_0.scrollLeft / track_0.clientWidth);
      positionRef.current = looping ? jumpOffEdgeCopy(track_0, position, slideCount) : position;
      const realSlide = positionRef.current - offset;
      if (realSlide !== navigatedSlideRef.current) {
        navigatedSlideRef.current = realSlide;
        trackCarouselNavigated(PDP_CAROUSEL.MEASURE_FOR_DELIVERY, realSlide + 1);
      }
    };
    $[15] = looping;
    $[16] = offset;
    $[17] = slideCount;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const handleScrollEnd = t11;
  let t12;
  if ($[19] !== handleScrollEnd || $[20] !== offset || $[21] !== slideCount || $[22] !== trackSlides.length) {
    t12 = () => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => {
        const track_1 = trackRef.current;
        if (track_1?.clientWidth) {
          const position_0 = Math.min(Math.max(Math.round(track_1.scrollLeft / track_1.clientWidth), 0), trackSlides.length - 1);
          setIndex((position_0 - offset + slideCount) % slideCount);
        }
      });
      clearTimeout(scrollEndRef.current);
      scrollEndRef.current = setTimeout(handleScrollEnd, 150);
    };
    $[19] = handleScrollEnd;
    $[20] = offset;
    $[21] = slideCount;
    $[22] = trackSlides.length;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handleScroll = t12;
  let t13;
  if ($[24] !== looping || $[25] !== slideCount) {
    t13 = direction => {
      const track_2 = trackRef.current;
      if (!track_2) {
        return;
      }
      const position_1 = looping ? jumpOffEdgeCopy(track_2, positionRef.current, slideCount) + direction : (positionRef.current + direction + slideCount) % slideCount;
      positionRef.current = position_1;
      track_2.scrollTo({
        left: position_1 * track_2.clientWidth,
        behavior: prefersReducedMotion() ? "auto" : "smooth"
      });
    };
    $[24] = looping;
    $[25] = slideCount;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const step = t13;
  let t14;
  if ($[27] !== index || $[28] !== offset || $[29] !== trackSlides) {
    let t15;
    if ($[31] !== index || $[32] !== offset) {
      t15 = (t16, position_2) => {
        const {
          key,
          imageSrc,
          imageAlt
        } = t16;
        return <div key={key} aria-hidden={position_2 !== index + offset} className="h-full w-full flex-none snap-center"><JBImage className="h-full w-full object-cover" src={imageSrc} alt={imageAlt} sizes="(max-width: 767px) 100vw, 30vw" /></div>;
      };
      $[31] = index;
      $[32] = offset;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    t14 = trackSlides.map(t15);
    $[27] = index;
    $[28] = offset;
    $[29] = trackSlides;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[34] !== handleScroll || $[35] !== t14) {
    t15 = <div ref={trackRef} onScroll={handleScroll} className="flex h-full snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{t14}</div>;
    $[34] = handleScroll;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== step) {
    t16 = <ArrowButton direction="prev" onClick={() => step(-1)} />;
    $[37] = step;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== step) {
    t17 = <ArrowButton direction="next" onClick={() => step(1)} />;
    $[39] = step;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t16 || $[42] !== t17) {
    t18 = <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between px-2">{t16}{t17}</div>;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t15 || $[45] !== t18) {
    t19 = <div className="relative aspect-video">{t15}{t18}</div>;
    $[44] = t15;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  const t20 = `${index + 1} of ${slideCount}`;
  let t21;
  if ($[47] !== slide.heading) {
    t21 = <h4 aria-live="polite" className="m-0">{slide.heading}</h4>;
    $[47] = slide.heading;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const t22 = expanded ? "Read Less" : "Read More";
  let t23;
  if ($[49] !== bodyId || $[50] !== expanded || $[51] !== t22 || $[52] !== toggleBody) {
    t23 = <button ref={readMoreRef} type="button" aria-expanded={expanded} aria-controls={bodyId} onClick={toggleBody} className="inline-flex h-11 flex-none items-center border-0 bg-transparent p-0 text-xs font-bold text-brand no-underline hover:cursor-pointer hover:underline">{t22}</button>;
    $[49] = bodyId;
    $[50] = expanded;
    $[51] = t22;
    $[52] = toggleBody;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t21 || $[55] !== t23) {
    t24 = <div className="flex items-center justify-between gap-4">{t21}{t23}</div>;
    $[54] = t21;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  const t25 = !expanded;
  let t26;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => closeBody({
      restoreFocus: true
    });
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <button type="button" aria-label="Close" onClick={t26} className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center border-0 bg-transparent p-0 hover:cursor-pointer"><IconClose width={9} height={9} /></button>;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== slide.bodyHtml) {
    t28 = <SanitizedHtml html={slide.bodyHtml} />;
    $[59] = slide.bodyHtml;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== bodyId || $[62] !== t25 || $[63] !== t28) {
    t29 = <div id={bodyId} hidden={t25} className="absolute inset-x-0 top-full z-10 border border-solid border-gray-light6 bg-white py-4 pl-4 pr-11 shadow-md">{t27}{t28}</div>;
    $[61] = bodyId;
    $[62] = t25;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t20 || $[66] !== t24 || $[67] !== t29) {
    t30 = <div aria-roledescription="slide" aria-label={t20} role="group" className="relative">{t24}{t29}</div>;
    $[65] = t20;
    $[66] = t24;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t19 || $[70] !== t30) {
    t31 = <section ref={sectionRef} aria-roledescription="carousel" aria-label="Measuring for Delivery" data-xc="PDPMeasureForDeliverySlideshow">{t19}{t30}</section>;
    $[69] = t19;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  return t31;
};
FitGuideSlideshow.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.shape({
    heading: PropTypes.string.isRequired,
    bodyHtml: PropTypes.string.isRequired,
    imageSrc: PropTypes.string.isRequired,
    imageAlt: PropTypes.string
  })).isRequired
};
const FitGuideSlideshowFromCMS = () => {
  const $ = _c(2);
  const slides = useFitGuideSlides();
  let t0;
  if ($[0] !== slides) {
    t0 = slides.length > 0 ? <FitGuideSlideshow key={slides.length} slides={slides} /> : null;
    $[0] = slides;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};

// Own boundary: the next one up wraps every info tab
const PDPMeasureForDeliverySlideshow = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={null}><FitGuideSlideshowFromCMS /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export default PDPMeasureForDeliverySlideshow;
function _temp(open) {
  return !open;
}